import dayjs from "dayjs";
import { api } from "@service/api";
import { Masks } from "@hooks/functions/Masks/Masks";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { useEffect, useState } from "react";
import { FiClock, FiExternalLink, FiDollarSign } from "react-icons/fi";
import { FaCheckCircle } from "react-icons/fa";

interface IProps {
  raffleId: string;
  isOpen: boolean;
  onClose: () => void;
  onPaid: (paid: boolean, timeToClose: number) => void;
}

interface CheckoutData {
  dateExp: Date;
  url: string;
  price: number;
}

interface TimeRemaining {
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

const masks = new Masks();
export function ModalRafflePayment({
  raffleId,
  isOpen,
  onClose,
  onPaid,
}: IProps) {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const timeToClose = 3500;
  const [paid, setPaid] = useState<boolean>(false);
  const [animate, setAnimate] = useState(false);
  const [checkout, setCheckout] = useState<CheckoutData>();

  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    hours: 0,
    minutes: 0,
    seconds: 0,
    expired: false,
  });

  function handleClose() {
    setAnimate(false);
    setTimeout(() => onClose(), 300);
  }

  function handleEscape(evt: KeyboardEvent) {
    if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
      handleClose();
    }
  }

  const calculateTimeRemaining = (expirationDate: Date): TimeRemaining => {
    const now = new Date().getTime();
    const expTime = new Date(expirationDate).getTime();
    const difference = expTime - now;

    if (difference <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    const hours = Math.floor(difference / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return { hours, minutes, seconds, expired: false };
  };

  const handleRaffleCheckOut = async ({ raffleId }: { raffleId: string }) => {
    try {
      const params = `?key=${raffleId}`;
      const response = await api.get(
        `/customer/raffle/default/my/checkout${params}`,
      );
      setCheckout(response.data);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message || "Falha ao carregar as informações.",
        type: "error",
        theme: "dark",
      });
    }
  };

  const handleRaffleCheckStatus = async ({
    raffleId,
  }: {
    raffleId: string;
  }) => {
    if (paid) return;
    try {
      const params = `?key=${raffleId}`;
      const response = await api.get(`/customer/raffle/primaryInfo${params}`);

      const _paid = response?.data?.paid || false;
      setPaid(_paid);

      // Looping to Check Raffle Status
      if (!_paid) {
        setTimeout(() => {
          handleRaffleCheckStatus({ raffleId });
        }, 10000);
      } else {
        pushToastify({
          message: _paid
            ? "Pagamento realizado com sucesso."
            : "Pagamento não processado ainda.",
          type: _paid ? "success" : "error",
          theme: "dark",
        });
      }
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message || "Falha ao carregar as informações.",
        type: "error",
        theme: "dark",
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);

      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!checkout?.dateExp) return;

    // --- Update immediately --- //
    setTimeRemaining(calculateTimeRemaining(checkout.dateExp));

    // --- Update every second --- //
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining(checkout.dateExp);
      setTimeRemaining(remaining);

      if (remaining.expired) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [checkout?.dateExp]);

  useEffect(() => {
    if (paid) {
      onPaid(paid, timeToClose);

      setTimeout(() => {
        handleClose();
      }, timeToClose);
    }
  }, [paid]);

  useEffect(() => {
    if (isOpen) {
      if (raffleId) {
        handleRaffleCheckOut({ raffleId });

        // --- Check if Raffle was Paid --- //
        setTimeout(() => {
          handleRaffleCheckStatus({ raffleId });
        }, 10000);
      }
    }
  }, [isOpen]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="checkoutModalLabel"
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold" id="checkoutModalLabel">
                Pagamento da Campanha
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => handleClose()}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body px-4 py-4">
              {/* Payment Amount */}
              {checkout && (
                <div className="alert alert-info d-flex align-items-center justify-content-between mb-4">
                  <div className="d-flex align-items-center">
                    <FiDollarSign className="me-2" size={20} />
                    <strong>Valor do Pagamento</strong>
                  </div>
                  <div className="fs-4 fw-bold text-primary">
                    {masks.brlCurrency(checkout.price)}
                  </div>
                </div>
              )}

              {paid ? (
                <>
                  <div
                    className={`alert alert-success d-flex align-items-center mb-4`}
                  >
                    <FaCheckCircle className="me-2 text-success" size={20} />
                    <div className="flex-grow-1">
                      <strong>Pagamento realizado com sucesso.</strong>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Expiration Alert */}
                  <div
                    className={`alert ${
                      timeRemaining.expired ? "alert-danger" : "alert-warning"
                    } d-flex align-items-center mb-4`}
                  >
                    <FiClock className="me-2" size={20} />
                    <div className="flex-grow-1">
                      {timeRemaining.expired ? (
                        <strong>Pagamento Expirado</strong>
                      ) : (
                        <>
                          <strong>Tempo restante para pagamento</strong>
                          <div className="mt-1">
                            Expira em:{" "}
                            {checkout
                              ? dayjs(checkout.dateExp).format(
                                  "DD/MM/YYYY [às] HH:mm:ss",
                                )
                              : "~~"}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Countdown Timer */}
                  {!timeRemaining.expired && (
                    <div className="text-center mb-4 py-3 bg-light rounded">
                      <div className="d-flex justify-content-center align-items-center gap-3">
                        <div className="d-flex flex-column align-items-center">
                          <div className="display-4 fw-bold text-primary">
                            {String(timeRemaining.hours).padStart(2, "0")}
                          </div>
                          <small className="text-muted">Horas</small>
                        </div>

                        <div
                          className="display-4 fw-bold text-muted"
                          style={{ marginBottom: "20px" }}
                        >
                          :
                        </div>

                        <div className="d-flex flex-column align-items-center">
                          <div className="display-4 fw-bold text-primary">
                            {String(timeRemaining.minutes).padStart(2, "0")}
                          </div>
                          <small className="text-muted">Minutos</small>
                        </div>

                        <div
                          className="display-4 fw-bold text-muted"
                          style={{ marginBottom: "20px" }}
                        >
                          :
                        </div>

                        <div className="d-flex flex-column align-items-center">
                          <div className="display-4 fw-bold text-primary">
                            {String(timeRemaining.seconds).padStart(2, "0")}
                          </div>
                          <small className="text-muted">Segundos</small>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Information */}
              <div className="mb-4 d-flex flex-column text-center">
                {paid ? (
                  <>
                    <span className="text-muted">
                      Seu pagamento foi confirmado com sucesso.
                    </span>
                    <span className="text-muted">
                      Esta janela se fechará automaticamente em instantes.
                    </span>
                  </>
                ) : (
                  <>
                    {timeRemaining.expired ? (
                      <>
                        <span className="text-muted">
                          O link de pagamento expirou.
                        </span>
                        <span className="text-muted">
                          Por favor, gere um novo link para continuar.
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-muted">
                          Complete o pagamento para ativar sua campanha.
                        </span>
                        <span className="text-muted">
                          O link de pagamento expirará automaticamente no
                          horário indicado.
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-2 justify-content-end">
                <button
                  onClick={() => handleClose()}
                  className="btn btn-outline-secondary px-4"
                >
                  Fechar
                </button>
                {!paid && (
                  <button
                    onClick={() => {
                      if (timeRemaining.expired) {
                        handleRaffleCheckOut({ raffleId });
                      } else {
                        if (checkout) {
                          window.open(
                            checkout.url,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }
                      }
                    }}
                    className="btn btn-primary px-4 d-flex align-items-center gap-2"
                  >
                    {timeRemaining.expired ? (
                      "Gerar Novo Link"
                    ) : (
                      <>
                        Ir para Pagamento
                        <FiExternalLink size={18} />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      ></div>
    </>
  );
}
